import React, { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { getCurrentUser, signOut } from "aws-amplify/auth";
import { OnboardingAPI, OnboardingApiError } from "../services/onboarding.service";
import { logger } from "../utils/logger";
import { clearPhiTables } from "../db/security-lifecycle";
import * as SyncManager from "../sync/sync-manager";
import { ONBOARDING_STATUS_ROUTES, TOS_VERSION, PP_VERSION } from "../constants";
import DeviceVerificationScreen from "./auth/DeviceVerificationScreen";

const STEP_ORDER = [
  "clinic_details_pending",
  "documents_pending",
  "email_verification_pending",
] as const;

const ROUTE_TO_STATUS: Record<string, string> = {
  "/onboarding/clinic-details": "clinic_details_pending",
  "/onboarding/documents": "documents_pending",
  "/onboarding/select-plan": "email_verification_pending",
};

const CHECK_TIMEOUT_MS = 30_000;

/**
 * Where a user whose onboarding is not complete must be sent, or null when the
 * current route is allowed: their own step, or an earlier one they may revisit.
 * Shared by the cached fast path and the online check so the two cannot drift;
 * the fast path used to redirect only, never authorize, and left users on their
 * own step stuck on "Checking session..." (prod, 2026-10-22).
 */
export function pendingOnboardingRedirect(pathname: string, status: string): string | null {
  const pendingRoute = ONBOARDING_STATUS_ROUTES[status] ?? "/onboarding/clinic-details";
  if (!pathname.startsWith("/onboarding")) return pendingRoute;
  const routeStatus = ROUTE_TO_STATUS[pathname];
  if (!routeStatus) return null;
  const userStep = STEP_ORDER.indexOf(status as (typeof STEP_ORDER)[number]);
  const routeStep = STEP_ORDER.indexOf(routeStatus as (typeof STEP_ORDER)[number]);
  return routeStep > userStep ? pendingRoute : null;
}

/**
 * "Add a clinic" reuses the clinic-details form in branch mode. Only an Admin
 * who has already finished onboarding can add a branch, so the completed-user
 * bounce to /dashboard must let this one route through or the feature is
 * unreachable. Every other onboarding route still bounces.
 */
function isBranchSetupRoute(pathname: string, search: string, role: string): boolean {
  return (
    role === "Admin" &&
    pathname === "/onboarding/clinic-details" &&
    new URLSearchParams(search).get("mode") === "branch"
  );
}

// ─── Offline auth cache ───────────────────────────────────────────────────────
// Two-layer cache: in-memory for fast within-session navigation, localStorage
// for cross-session persistence (survives page reloads while offline).
//
// TTL: 72 h on the localStorage cache. After that the user must be online for
// a fresh role/status check — bounds the offline window for a suspended account.

// In-memory shortcut: populated after a successful online check so offline
// navigation within the same session never re-hits the network.
let _sessionCache: { role: string; onboarding_status: string } | null = null;

export const AUTH_CACHE_KEY    = "kaagazy_auth_cache";
export const AUTH_CACHE_TTL_MS = 72 * 60 * 60 * 1000;
export const AUTH_USER_ID_KEY  = "kaagazy_auth_user_id";

export interface AuthCache {
  userId:            string;
  role:              string;
  onboarding_status: string;
  cachedAt:          number;
}

export function readAuthCache(userId: string): AuthCache | null {
  try {
    const raw = localStorage.getItem(AUTH_CACHE_KEY);
    if (!raw) return null;
    const parsed: AuthCache = JSON.parse(raw);
    if (parsed.userId !== userId) return null;
    if (Date.now() - parsed.cachedAt > AUTH_CACHE_TTL_MS) {
      localStorage.removeItem(AUTH_CACHE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function writeAuthCache(userId: string, role: string, onboarding_status: string): void {
  try {
    const raw = localStorage.getItem(AUTH_CACHE_KEY);
    const existing = raw ? JSON.parse(raw) : {};
    const entry: AuthCache = { ...existing, userId, role, onboarding_status, cachedAt: Date.now() };
    localStorage.setItem(AUTH_CACHE_KEY, JSON.stringify(entry));
    localStorage.setItem(AUTH_USER_ID_KEY, userId);
    _sessionCache = { role, onboarding_status };
  } catch { /* storage quota exceeded — non-fatal */ }
}

/** Called on signOut so the next user on this device starts with no cached role. */
export function clearAuthCache(): void {
  try {
    localStorage.removeItem(AUTH_CACHE_KEY);
    localStorage.removeItem(AUTH_USER_ID_KEY);
  } catch { /* non-fatal */ }
  _sessionCache = null;
}

export function getSessionCache(): { role: string; onboarding_status: string } | null {
  if (_sessionCache) return _sessionCache;
  const storedUserId = localStorage.getItem(AUTH_USER_ID_KEY);
  if (!storedUserId) return null;
  const cached = readAuthCache(storedUserId);
  if (cached && cached.role && cached.onboarding_status) {
    _sessionCache = { role: cached.role, onboarding_status: cached.onboarding_status };
    return _sessionCache;
  }
  return null;
}

/**
 * True when the signed-in user is a PlatformAdmin. They have no clinic, so the
 * backend refuses every clinic-scoped endpoint: clinic pollers (sync pull,
 * wallet, reference data) must not run for them. A parked PlatformAdmin tab
 * sent about 50 of those 403s every 4 hours (prod, 2026-11-02) and kept the
 * auth-4xx alarm flapping. An unknown role is not PlatformAdmin, so a clinic
 * user is never left without sync.
 */
export function isPlatformAdminSession(): boolean {
  return getSessionRole() === "PlatformAdmin";
}

/**
 * Re-reads onboarding_status into the auth cache. Every onboarding step that
 * advances the status server-side must await this before navigating on: the
 * cached fast path authorizes the next route against the cached status, so a
 * stale one bounced the user back to the step they had just saved and only
 * their second click went through (prod, 2026-11-02). If the status cannot be
 * read, the cache is dropped so RequireAuth runs its online check instead.
 */
export async function refreshOnboardingCache(): Promise<void> {
  const session = getSessionCache();
  const userId = localStorage.getItem(AUTH_USER_ID_KEY);
  if (!session || !userId) return;
  try {
    const { onboarding_status } = await OnboardingAPI.getStatus();
    writeAuthCache(userId, session.role, onboarding_status);
  } catch {
    clearAuthCache();
  }
}

/**
 * Current user's role ("Admin" | "Doctor" | "Receptionist"), or null if unknown.
 *
 * Reads the in-memory session cache first (populated after the online auth
 * check) and falls back to the persisted localStorage cache so it works
 * offline and synchronously — no network call. RequireAuth always runs before
 * a protected page renders, so one of these is populated for any signed-in user.
 */
export function getSessionRole(): string | null {
  return getSessionCache()?.role ?? null;
}

/**
 * True when a background revalidation error proves the session is over.
 * A failed Cognito getCurrentUser is not an ApiError and always counts.
 * From the API: any 401, and any 403 except the device challenge.
 */
export function isDeadSession(err: unknown): boolean {
  if (!(err instanceof OnboardingApiError)) return true;
  if (err.statusCode === 401) return true;
  return err.statusCode === 403 && err.message !== "device_verification_required";
}

const RequireAuth: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [authorized, setAuthorized] = useState(() => {
    const session = getSessionCache();
    return session?.onboarding_status === "completed";
  });
  const [authError, setAuthError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [deviceVerificationRequired, setDeviceVerificationRequired] = useState(false);

  useEffect(() => {
    // Synchronous fast path — avoid the loading flash on every route change
    // or page reload once a session is cached.
    //
    // A background revalidation fires immediately: getCurrentUser + getMe are
    // checked against Cognito, and if the account was suspended or the session
    // revoked, the user is redirected within seconds rather than riding the
    // 72 h localStorage TTL. This satisfies the PHI-wipe-reachability concern.
    const cachedSession = getSessionCache();
    if (cachedSession) {
      if (cachedSession.onboarding_status === "completed") {
        setAuthorized(true);
        setAuthError(null);
        if (
          location.pathname.startsWith("/onboarding") &&
          !isBranchSetupRoute(location.pathname, location.search, cachedSession.role)
        ) {
          navigate("/dashboard", { replace: true });
        }
      } else {
        const redirect = pendingOnboardingRedirect(location.pathname, cachedSession.onboarding_status);
        if (redirect) {
          navigate(redirect, { replace: true });
        } else {
          setAuthorized(true);
          setAuthError(null);
        }
      }

      // Background revalidation: verify the session is still valid with Cognito
      // so a suspended/revoked account doesn't ride the cache for 72 h.
      if (navigator.onLine) {
        const revalidate = async () => {
          try {
            const cognitoUser = await getCurrentUser();
            const meResult = await OnboardingAPI.getMe();
            if (!meResult.role) {
              // Role removed — force role selection.
              _sessionCache = null;
              clearAuthCache();
              navigate("/auth/role", { replace: true });
              return;
            }
            // Re-fetch onboarding status to catch any backend-side changes.
            try {
              const { onboarding_status } = await OnboardingAPI.getStatus();
              writeAuthCache(cognitoUser.userId, meResult.role, onboarding_status);
            } catch {
              // getStatus failure during background revalidation is non-fatal;
              // the cache remains valid from the initial check.
              writeAuthCache(cognitoUser.userId, meResult.role, cachedSession.onboarding_status);
            }
          } catch (err) {
            // Only a dead session signs the user out. A 403 device challenge is
            // already being handled: api.client dispatched the event that shows
            // DeviceVerificationScreen. Signing out here raced that screen and
            // left a blank page whenever the browser had lost its device id
            // (prod, 2026-10-22). A network or 5xx failure is not proof either.
            if (!isDeadSession(err)) {
              logger.warn("RequireAuth: background revalidation inconclusive, keeping session", {
                status: err instanceof OnboardingApiError ? err.statusCode : undefined,
              });
              return;
            }
            logger.warn("RequireAuth: background revalidation failed — session no longer valid");
            _sessionCache = null;
            clearAuthCache();
            clearPhiTables();
            try { await signOut(); } catch { /* best-effort */ }
            navigate("/login", { replace: true });
          }
        };
        void revalidate();
      }
      return;
    }

    let cancelled = false;
    setAuthorized(false);
    setAuthError(null);

    const timeoutId = setTimeout(() => {
      if (!cancelled) {
        cancelled = true;
        setAuthError(
          "Session check is taking too long. Please retry or log out."
        );
      }
    }, CHECK_TIMEOUT_MS);

    const check = async () => {
      const initialCache = getSessionCache();
      if (initialCache) {
        if (!cancelled) {
          if (initialCache.onboarding_status === "completed") {
            setAuthorized(true);
          } else {
            navigate(ONBOARDING_STATUS_ROUTES[initialCache.onboarding_status] ?? "/onboarding/clinic-details", { replace: true });
          }
        }
        clearTimeout(timeoutId);
        return;
      }

      // Offline: use localStorage cache to avoid any network calls.
      if (!navigator.onLine) {
        const cachedUserId = localStorage.getItem(AUTH_USER_ID_KEY);
        if (cachedUserId) {
          const cached = readAuthCache(cachedUserId);
          if (cached) {
            _sessionCache = { role: cached.role, onboarding_status: cached.onboarding_status };
            if (!cancelled) {
              if (cached.onboarding_status === "completed") {
                setAuthorized(true);
              } else {
                navigate(ONBOARDING_STATUS_ROUTES[cached.onboarding_status] ?? "/onboarding/clinic-details", { replace: true });
              }
            }
            clearTimeout(timeoutId);
            return;
          }
        }
        // No cache — fall through; network call will fail fast and show the error UI.
      }

      // Sequential: getMe() only called if getCurrentUser() succeeds.
      let cognitoUser: Awaited<ReturnType<typeof getCurrentUser>>;
      try {
        cognitoUser = await getCurrentUser();
      } catch {
        if (cancelled) return;
        _sessionCache = null;
        clearPhiTables();
        navigate("/login", { replace: true });
        return;
      }

      const userCached = readAuthCache(cognitoUser.userId);
      if (userCached) {
        _sessionCache = { role: userCached.role, onboarding_status: userCached.onboarding_status };
        if (!cancelled) {
          if (userCached.onboarding_status === "completed") {
            setAuthorized(true);
          } else {
            navigate(ONBOARDING_STATUS_ROUTES[userCached.onboarding_status] ?? "/onboarding/clinic-details", { replace: true });
          }
        }
        clearTimeout(timeoutId);
        return;
      }

      let meResult: Awaited<ReturnType<typeof OnboardingAPI.getMe>>;
      try {
        meResult = await OnboardingAPI.getMe();
      } catch (err) {
        if (cancelled) return;
        const status = err instanceof OnboardingApiError ? err.statusCode : 0;

        if (status === 404) {
          logger.warn("RequireAuth: getMe() returned 404 — user record missing from DB, redirecting to role selection");
          navigate("/auth/role", { replace: true });
          return;
        }

        if (status === 403 && err instanceof OnboardingApiError && err.message === "device_verification_required") {
          if (!cancelled) setDeviceVerificationRequired(true);
          clearTimeout(timeoutId);
          return;
        }

        if (status === 401 || status === 403) {
          navigate("/login", { replace: true });
          return;
        }

        // Network error — Cognito session valid, use localStorage fallback.
        const cached = readAuthCache(cognitoUser.userId);
        if (cached) {
          logger.warn("RequireAuth: offline — using cached auth", { status });
          if (cached.onboarding_status === "completed") {
            setAuthorized(true);
          } else {
            navigate(ONBOARDING_STATUS_ROUTES[cached.onboarding_status] ?? "/onboarding/clinic-details", { replace: true });
          }
          return;
        }

        logger.error("RequireAuth: getMe() failed and no offline cache", undefined, { status });
        setAuthError("Unable to reach the server. Please check your connection and try again.");
        return;
      }

      const { role } = meResult;
      if (!role) {
        if (!cancelled) navigate("/auth/role", { replace: true });
        return;
      }

      // PlatformAdmin has no clinic and no onboarding — skip getStatus() entirely
      if (role === "PlatformAdmin") {
        if (cancelled) return;
        writeAuthCache(cognitoUser.userId, role, "completed");
        _sessionCache = { role, onboarding_status: "completed" };
        if (!location.pathname.startsWith("/platform")) {
          navigate("/platform/kyc", { replace: true });
        } else {
          setAuthorized(true);
        }
        return;
      }

      let onboardingStatus: string;
      // True only when we've just confirmed (online, this check) that the
      // user has no clinic yet. Left false in the offline/network-error
      // fallback branches below, where we can't be sure either way.
      let clinicIdIsDefinitelyNull = false;
      try {
        const { onboarding_status, clinic_id } = await OnboardingAPI.getStatus();
        onboardingStatus = onboarding_status;
        clinicIdIsDefinitelyNull = !clinic_id;
      } catch (err) {
        if (cancelled) return;
        const status = err instanceof OnboardingApiError ? err.statusCode : 0;
        if (status === 404) {
          onboardingStatus = "clinic_details_pending";
          clinicIdIsDefinitelyNull = true;
        } else if (status === 0 || status === 503) {
          // Network error — use localStorage cache.
          const cached = readAuthCache(cognitoUser.userId);
          if (cached) {
            logger.warn("RequireAuth: getStatus() offline — using cached status");
            onboardingStatus = cached.onboarding_status;
          } else {
            logger.error("RequireAuth: getStatus() failed and no offline cache", undefined, { status });
            setAuthError("Unable to load your session. Please try again or log out and back in.");
            return;
          }
        } else {
          logger.error("RequireAuth: getStatus() failed", undefined, { status });
          setAuthError(
            "Unable to load your session. Please try again or log out and back in."
          );
          return;
        }
      }

      // A non-empty role does not mean it was ever genuinely assigned - device-
      // check's auto-create can seed a brand-new signup as "Doctor" before
      // role selection ever runs (see fix/rbac-new-signup-defaults-to-doctor).
      // RequireAuth wraps nearly every route in the app, so if this check
      // doesn't also self-heal, a seeded-Doctor user reaching ANY protected
      // route directly (refresh, deep link, back-forward-cache, a second tab)
      // bypasses /auth/role's own correction entirely and can go on to create
      // a clinic while still "Doctor", freezing the role permanently.
      if (clinicIdIsDefinitelyNull && role !== "Admin") {
        if (!cancelled) navigate("/auth/role", { replace: true });
        return;
      }

      // Persist for next offline visit
      writeAuthCache(cognitoUser.userId, role, onboardingStatus);

      if (cancelled) return;

      // Persist the verified session so offline navigation doesn't re-hit the network.
      _sessionCache = { role, onboarding_status: onboardingStatus };

      // Best-effort: enrich the consent log with device details on every cold
      // session load while the user is still in onboarding. The backend call is
      // idempotent (WHERE device_details IS NULL guard) so this is safe to call
      // multiple times — it only triggers the consent confirmation email once.
      // This catches the case where the RoleProvisioningPage call was missed due
      // to a tab close or network drop.
      if (onboardingStatus !== "completed") {
        void OnboardingAPI.recordConsentDetails({
          screen_width: window.screen.width,
          screen_height: window.screen.height,
          method: "email_signup",
          client_tos_version: TOS_VERSION,
          client_pp_version: PP_VERSION,
        }).catch(() => {});
      }

      const isOnboardingRoute = location.pathname.startsWith("/onboarding");

      // The clinic picker is exempt from the onboarding redirect below.
      //
      // An org owner who switches into a half-set-up branch gets force-routed
      // into its onboarding, and those pages render no sidebar — so no clinic
      // switcher, and their OTHER working clinic becomes unreachable. Letting
      // /select-clinic through is the way back: pick a different clinic and
      // carry on. Without this exemption, navigating there just bounces
      // straight back into the branch's setup.
      //
      // Safe for a genuine first-time signup: they have one clinic, so
      // RequireClinicSelection sends them onward and the redirect below
      // applies as normal.
      if (location.pathname === "/select-clinic") {
        setAuthorized(true);
        return;
      }

      if (onboardingStatus === "completed") {
        if (isOnboardingRoute && !isBranchSetupRoute(location.pathname, location.search, role)) {
          navigate("/dashboard", { replace: true });
          return;
        }
        setAuthorized(true);
        return;
      }

      const redirect = pendingOnboardingRedirect(location.pathname, onboardingStatus);
      if (redirect) {
        navigate(redirect, { replace: true });
        return;
      }

      setAuthorized(true);
    };

    check().finally(() => clearTimeout(timeoutId));

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [location.pathname, location.search, navigate, retryCount]);

  useEffect(() => {
    if (!authorized) return;
    window.dispatchEvent(new Event("userLoggedIn"));
    if (isPlatformAdminSession()) return;
    SyncManager.start();
    return () => { SyncManager.stop(); };
  }, [authorized]);

  // Mid-session: any API call that returns 403 device_verification_required
  // or 401 session_revoked re-gates the user without a full page reload.
  useEffect(() => {
    const onDeviceChallenge = () => {
      _sessionCache = null;
      setAuthorized(false);
      setDeviceVerificationRequired(true);
    };
    const onSessionRevoked = async () => {
      _sessionCache = null;
      clearAuthCache();
      try { await signOut(); } catch { /* best-effort */ }
      navigate("/login", { replace: true });
    };
    window.addEventListener("kaagazy:device-verification-required", onDeviceChallenge);
    window.addEventListener("kaagazy:session-revoked", onSessionRevoked);
    return () => {
      window.removeEventListener("kaagazy:device-verification-required", onDeviceChallenge);
      window.removeEventListener("kaagazy:session-revoked", onSessionRevoked);
    };
  }, [navigate]);

  if (deviceVerificationRequired) {
    return (
      <DeviceVerificationScreen
        onVerified={() => {
          setDeviceVerificationRequired(false);
          setRetryCount(c => c + 1);
        }}
      />
    );
  }

  if (authError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F5F7]">
        <div className="max-w-sm w-full mx-4 rounded-2xl bg-white shadow-sm border border-slate-200 p-8 text-center space-y-4">
          <p className="text-sm text-slate-600">{authError}</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => {
                setAuthError(null);
                setRetryCount(c => c + 1);
              }}
              className="rounded-full bg-[#0066ff] px-5 py-2 text-sm font-medium text-white hover:bg-[#0051cc]"
            >
              Retry
            </button>
            <button
              onClick={async () => {
                try {
                  await signOut();
                } catch {
                  // best-effort; still redirect even if signOut fails
                }
                navigate("/login", { replace: true });
              }}
              className="rounded-full border border-slate-200 px-5 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
            >
              Log out
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!authorized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F5F7]">
        <div className="text-sm text-slate-500">Checking session…</div>
      </div>
    );
  }

  return <Outlet />;
};

export default RequireAuth;
